import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useListSearchParams } from '@/hooks/useListSearchParams'
import { useGenerationAttempts } from '../api/admin-api'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${month}-${day} ${hours}:${minutes}`
}

export function FailedImagesList() {
  const navigate = useNavigate()
  const { page, filters, setPage, setFilter } = useListSearchParams({ search: '' })
  const [searchInput, setSearchInput] = useState(filters.search)

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilter('search', searchInput)
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [searchInput, filters.search, setFilter])

  const { data, isLoading, error } = useGenerationAttempts({
    page,
    pageSize: 20,
    search: filters.search || undefined,
    sortBy: 'created_at',
    sortOrder: 'desc',
  })

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">로딩 중...</div>
  }

  if (error) {
    return <div className="text-center py-12 text-red-500">에러: {error.message}</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-heading tracking-tight">실패한 이미지</h1>
          <p className="text-caption mt-2 leading-relaxed">
            이미지 생성에 실패한 요청 이력을 확인합니다
          </p>
        </div>
      </div>

      <div className="flex items-center space-x-4">
        <Input
          placeholder="프롬프트/사용자/에러 검색..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="max-w-xs"
        />
      </div>

      <div className="bg-white rounded-lg border border-gray-200 min-w-[800px]">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                프롬프트
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                요청/성공/실패
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                템플릿/스타일
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                생성일시
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data?.items.map((attempt) => {
              return (
                <tr
                  key={attempt.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/admin/failed-images/${attempt.id}/view`)}
                >
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900 line-clamp-2">{attempt.prompt}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      {attempt.user?.email ?? '-'}
                    </div>
                    {attempt.errorSummary && (
                      <div className="mt-1.5 px-2 py-1 bg-red-50 text-red-700 text-xs rounded line-clamp-2">
                        {attempt.errorSummary}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                    {attempt.requestedCount} / {attempt.successCount} / {attempt.failureCount}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    <div>{attempt.template?.name ?? '-'}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      스타일: {attempt.style || '없음'}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                    {formatDate(attempt.createdAt)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {data?.items.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            실패한 이미지 생성 이력이 없습니다
          </div>
        )}
      </div>

      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
          >
            이전
          </Button>
          <span className="text-sm text-gray-600">
            {page} / {data.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(Math.min(data.totalPages, page + 1))}
            disabled={page === data.totalPages}
          >
            다음
          </Button>
        </div>
      )}
    </div>
  )
}
